import { Icon, Tooltip } from '@assembly-js/design-system';
import type { IconType as AssemblyIconType } from '@assembly-js/design-system';
import {
  createStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  SvgIcon,
} from '@material-ui/core';
import clsx from 'clsx';
import React, {
  ComponentProps,
  CSSProperties,
  PropsWithChildren,
  useContext,
} from 'react';
import { type IconType } from 'react-icons';

import {
  SidebarTheme,
  SidebarThemeContext,
  useFeatureFlags,
} from 'src/context';

import { defaultListItemStyles, DynamicListItem } from './DynamicListItem';

const useStyles = makeStyles(() =>
  createStyles({
    listItemIconContainer: {
      color: (props: SidebarTheme) => props.sidebarTextColor,
    },
    listItemIconStrokeWidth: {
      '& path': {
        strokeWidth: 2,
      },
    },
    listItemText: {
      color: (props: SidebarTheme) => props.sidebarTextColor,
    },
    listItem: {
      '&:hover': {
        backgroundColor: (props: SidebarTheme) =>
          props.sidebarItemHoverBackground,
      },
      '&:hover svg': {
        color: (props: SidebarTheme) => props.sidebarTextColor,
      },
      '&:focus': {
        background: 'transparent',
      },
    },
    selected: {
      backgroundColor: (props: SidebarTheme) =>
        `${props.sidebarItemHoverBackground} !important`,
    },
  }),
);

type IconElementType = IconType | typeof SvgIcon;

export interface SidebarListItemIconProps {
  className?: string;
  IconElement?: IconElementType;
  iconName?: string;
  iconSettings?: {
    ignoreStrokeWidth?: boolean;
    className?: string;
  };
  iconStyleProps?: CSSProperties;
}

export const SidebarListItemIcon = ({
  className,
  IconElement,
  iconName,
  iconSettings = {},
  iconStyleProps = {},
}: SidebarListItemIconProps) => {
  const { EnableIconPicker } = useFeatureFlags();
  const sidebarTheme = useContext(SidebarThemeContext);
  const classes = useStyles(sidebarTheme);

  if (EnableIconPicker) {
    if (!iconName) {
      return null;
    }
    return (
      <ListItemIcon
        classes={{
          root: clsx('h-auto', classes.listItemIconContainer, className),
        }}
      >
        <Icon
          icon={iconName as AssemblyIconType}
          className={clsx('h-[14px] w-[14px]', iconSettings?.className)}
        />
      </ListItemIcon>
    );
  }

  if (!IconElement) {
    return null;
  }
  return (
    <ListItemIcon
      classes={{
        root: clsx('h-auto', classes.listItemIconContainer, className),
      }}
    >
      <IconElement
        style={iconStyleProps}
        className={clsx(
          'w-3.5 h-3.5',
          !iconSettings.ignoreStrokeWidth && classes.listItemIconStrokeWidth,
          iconSettings?.className,
        )}
      />
    </ListItemIcon>
  );
};

export const SidebarListItemText = ({
  className,
  text,
  bold = false,
}: {
  className?: string;
  text?: string;
  bold?: boolean;
}) => {
  const sidebarTheme = React.useContext(SidebarThemeContext);
  const classes = useStyles(sidebarTheme);
  return (
    <ListItemText
      primary={text}
      classes={{
        root: clsx('my-0.5', className),
        primary: clsx(
          'text-[13px] truncate',
          classes.listItemText,
          bold ? 'leading-6 font-medium' : 'leading-5',
        ),
      }}
    />
  );
};

interface SidebarListItemContainerProps {
  htmlId: string;
  selected?: boolean;
  handleClick?: ComponentProps<typeof ListItem>['onClick'];
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
}

export const SidebarListItemContainer = ({
  htmlId,
  selected = false,
  handleClick,
  children,
  tooltipTitle,
  disabled,
}: PropsWithChildren<SidebarListItemContainerProps>) => {
  const sidebarTheme = React.useContext(SidebarThemeContext);
  const classes = useStyles(sidebarTheme);

  return (
    <Tooltip
      tooltipClassname="h-6"
      content={tooltipTitle}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      baseOffset={0}
      offset={{ x: 14, y: 0 }}
      showDelayDuration={100}
    >
      <DynamicListItem
        disabled={disabled}
        htmlId={htmlId}
        data-testid={htmlId}
        classes={{ button: defaultListItemStyles }}
        className={clsx(classes.listItem, selected && classes.selected)}
        onClick={handleClick}
        button
      >
        {children}
      </DynamicListItem>
    </Tooltip>
  );
};

export interface SidebarListItemProps
  extends SidebarListItemContainerProps,
    Omit<SidebarListItemIconProps, 'IconElement'> {
  IconElement?: IconElementType;
  label?: string;
}

export const SidebarListItem = ({
  selected = false,
  label,
  IconElement,
  iconName,
  children,
  iconSettings,
  iconStyleProps,
  ...props
}: PropsWithChildren<SidebarListItemProps>) => (
  <SidebarListItemContainer selected={selected} {...props}>
    <SidebarListItemIcon
      className="min-w-6"
      IconElement={IconElement}
      iconSettings={iconSettings}
      iconStyleProps={iconStyleProps}
      iconName={iconName}
    />
    <SidebarListItemText text={label} bold={selected} />
    {children}
  </SidebarListItemContainer>
);
